import { useState, useEffect, useCallback } from 'react';

interface XPData {
  totalXP: number;
  level: number;
  xpToNextLevel: number;
  currentLevelXP: number;
}

interface XPBreakdown {
  base: number;
  win: number;
  mvp: number;
  efficiency: number;
  streak: number;
  challenges: number;
  total: number;
}

const STORAGE_KEY = '67chaos-xp';

// Level formula: XP required = level^2 * 100
function calculateLevel(totalXP: number): { level: number; xpToNextLevel: number; currentLevelXP: number } {
  let level = 1;
  let xpUsed = 0;
  
  while (true) {
    const xpForNextLevel = level * level * 100;
    if (xpUsed + xpForNextLevel > totalXP) {
      return {
        level,
        xpToNextLevel: xpForNextLevel - (totalXP - xpUsed),
        currentLevelXP: totalXP - xpUsed,
      };
    }
    xpUsed += xpForNextLevel;
    level++;
  }
}

function getXPForLevel(level: number): number {
  return level * level * 100;
}

export function useXP() {
  const [xpData, setXPData] = useState<XPData>({
    totalXP: 0,
    level: 1,
    xpToNextLevel: 100,
    currentLevelXP: 0,
  });

  // Load data on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const totalXP = parseInt(stored, 10);
        const levelData = calculateLevel(totalXP);
        setXPData({
          totalXP,
          ...levelData,
        });
      } catch {
        // Keep default
      }
    }
  }, []);

  // Calculate XP from a game
  const calculateGameXP = useCallback((params: {
    won: boolean;
    isMVP: boolean;
    rounds: number;
    players: number;
    streakBonus: number;
    challengeBonus: number;
  }): XPBreakdown => {
    const { won, isMVP, rounds, players, streakBonus, challengeBonus } = params;
    
    // Base XP for playing
    const base = 50;
    
    // Win bonus
    const win = won ? 100 : 0;
    
    // MVP bonus
    const mvp = isMVP ? 75 : 0;
    
    // Efficiency bonus (fewer rounds = more XP)
    let efficiency = 0;
    if (won) {
      if (rounds <= 3) efficiency = 100;
      else if (rounds <= 5) efficiency = 75;
      else if (rounds <= 7) efficiency = 50;
      else if (rounds <= 10) efficiency = 25;
    }
    
    // Player count multiplier (more players = slightly more XP)
    const playerMultiplier = 1 + (players - 2) * 0.1;
    
    const subtotal = Math.round((base + win + mvp + efficiency) * playerMultiplier);
    
    return {
      base,
      win,
      mvp,
      efficiency,
      streak: streakBonus,
      challenges: challengeBonus,
      total: subtotal + streakBonus + challengeBonus,
    };
  }, []);

  // Award XP and check for level up
  const awardXP = useCallback((amount: number): { leveledUp: boolean; newLevel: number } => {
    let leveledUp = false;
    let newLevel = xpData.level;
    
    setXPData(prev => {
      const newTotalXP = prev.totalXP + amount;
      const levelData = calculateLevel(newTotalXP);
      
      leveledUp = levelData.level > prev.level;
      newLevel = levelData.level;
      
      // Persist
      localStorage.setItem(STORAGE_KEY, String(newTotalXP));
      
      return {
        totalXP: newTotalXP,
        ...levelData,
      };
    });
    
    return { leveledUp, newLevel };
  }, [xpData.level]);

  // Get progress percentage to next level
  const getLevelProgress = useCallback((): number => {
    const xpForCurrentLevel = getXPForLevel(xpData.level);
    return (xpData.currentLevelXP / xpForCurrentLevel) * 100;
  }, [xpData]);

  return {
    xpData,
    calculateGameXP,
    awardXP,
    getLevelProgress,
  };
}
